import { checkBiometricAvailability } from '@shared/services/biometrics-auth.service';
import { isBiometricEnabled } from '@shared/services/biometrics-storage.service';
import { selectAuth } from '@shared/store/auth/auth.selectors';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to handle biometric authentication on cold start (app closed -> reopened)
 *
 * When the app is opened and tokens are restored from storage:
 * - Checks if biometrics are enabled
 * - Prompts for biometric confirmation (Face ID/Touch ID)
 * - Returns isWaitingForBiometricAuth flag to control loading state
 * - Logs out user if authentication fails or is cancelled
 */
export const useBiometricColdStart = (logout: () => Promise<void>) => {
  const auth = useSelector(selectAuth);
  const [hasCheckedBiometricOnStart, setHasCheckedBiometricOnStart] = useState(false);
  const [isWaitingForBiometricAuth, setIsWaitingForBiometricAuth] = useState(false);

  const checkBiometricOnColdStart = useCallback(async () => {
    // Only run once on mount
    if (hasCheckedBiometricOnStart) return;

    // Wait for auth state to stabilize
    // If not authenticated and not restored, we're still waiting for restoration to complete or fail
    if (!auth.isAuthenticated && !auth.wasRestoredFromStorage) return; // Don't set hasCheckedBiometricOnStart yet, let it run again

    // Now auth state is stable - either we restored from storage, or user needs to login
    if (!auth.wasRestoredFromStorage) {
      setHasCheckedBiometricOnStart(true);
      return;
    }

    // Check device capability
    const availability = await checkBiometricAvailability();
    if (!availability.isAvailable) {
      setHasCheckedBiometricOnStart(true);
      return;
    }

    // Check if user has enabled biometrics (read from storage directly)
    const enabled = await isBiometricEnabled();
    if (!enabled) {
      setHasCheckedBiometricOnStart(true);
      return;
    }

    // User has biometrics enabled - require confirmation to stay logged in
    setIsWaitingForBiometricAuth(true);

    try {
      const { authenticateWithBiometrics } = await import('@shared/services/biometrics-auth.service');
      const authResult = await authenticateWithBiometrics('Confirm your identity to continue');

      if (!authResult.success) {
        await logout(); // Logout user and clear all data
      }
    } catch (error) {
      console.error('[useBiometricColdStart] Biometric confirmation error, logging out user:', error);
      // Logout user on error
      await logout();
    } finally {
      setIsWaitingForBiometricAuth(false);
    }

    setHasCheckedBiometricOnStart(true);
  }, [auth.isAuthenticated, auth.wasRestoredFromStorage, hasCheckedBiometricOnStart, logout]);

  useEffect(() => {
    checkBiometricOnColdStart();
  }, [checkBiometricOnColdStart]);

  return { isWaitingForBiometricAuth };
};
